import { decodeJwtPayload } from '@klareospace/pharos-lib-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import type { PharosAuthState } from './pharos-auth-context';
import { SESSION_ENDED_REASON } from './session-ended-reason';
import type { SessionEndedReason } from './session-ended-reason';
import { SIGNED_OUT_STATE } from './signed-out-state';

// --- Constants ---

export const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

// --- Types ---

interface ImpersonationRefs {
  inactivityTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  klareoTokenRef: React.MutableRefObject<string | null>;
  sessionTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  tokenExpiryRef: React.MutableRefObject<number | null>;
}

export interface UseImpersonationSessionResult {
  /** Clear the impersonation session (used during sign-out) */
  clearImpersonation: () => void;
  /** Initial auth state computed synchronously from the token */
  initialState: PharosAuthState;
  /** Whether we're currently in impersonation mode */
  isImpersonating: boolean;
  /** The active impersonation token (null if not impersonating) */
  token: string | null;
}

// --- Helpers ---

/**
 * Compute the initial auth state synchronously.
 *
 * When an impersonation token is provided we decode it immediately and start
 * in an authenticated state. This is critical because TanStack Router's
 * `beforeLoad` guard runs synchronously on the very first render — if the
 * initial state were `{ isAuthenticated: false, isLoading: true }`, the guard
 * would redirect to `/login` before any `useEffect` has a chance to process
 * the token.
 */
export function buildInitialState(impersonationToken?: string | null): PharosAuthState {
  const baseState: PharosAuthState = { ...SIGNED_OUT_STATE, error: null, sessionEndedReason: null };

  if (!impersonationToken) {
    return { ...baseState, isLoading: true };
  }

  const payload = decodeJwtPayload(impersonationToken);
  if (!payload) {
    return { ...baseState, error: 'Invalid impersonation token' };
  }

  // Reject already-expired tokens synchronously so route guards never see
  // a brief authenticated state before the effect cleans up.
  const exp = payload.exp as number | undefined;
  if (exp && exp * 1000 <= Date.now()) {
    return { ...baseState, sessionEndedReason: SESSION_ENDED_REASON.MAX_DURATION };
  }

  const login = typeof payload.login === 'string' ? payload.login : '';

  return {
    ...baseState,
    isAuthenticated: true,
    isImpersonating: true,
    klareoToken: impersonationToken,
    user: { email: login, name: login },
  };
}

// --- Hook ---

/**
 * Encapsulates all impersonation-specific logic:
 * - Token state management (read once on mount via lazy initializer)
 * - Initial auth state computation
 * - JWT expiry timer setup
 * - Sign-out helper
 * - Cross-tab sign-out sync (`storage` event)
 *
 * The hook accepts a `setStateRef` (ref to the parent's setState) so it can trigger
 * session expiry transitions when the JWT exp timer fires. The ref is populated by
 * the parent after its own `useState` call, and is always current before any effect runs.
 */
export function useImpersonationSession(
  readImpersonationToken: (() => string | null) | undefined,
  refs: ImpersonationRefs,
  setStateRef: React.MutableRefObject<React.Dispatch<React.SetStateAction<PharosAuthState>>>,
): UseImpersonationSessionResult {
  const { inactivityTimerRef, klareoTokenRef, sessionTimerRef, tokenExpiryRef } = refs;

  // Read the token once synchronously on mount via lazy initializer.
  // Using state (not just a ref) ensures all dependent useEffect hooks re-run
  // when the token is cleared on sign-out, allowing the Firebase listener to re-register.
  const [token, setToken] = useState<string | null>(() => readImpersonationToken?.() ?? null);

  // Compute the initial auth state synchronously so TanStack Router's beforeLoad
  // guard sees the correct authenticated state on the very first render.
  // We use a ref to hold the initial state so it is only computed once.
  const initialStateRef = useRef<PharosAuthState | null>(null);
  if (initialStateRef.current === null) {
    initialStateRef.current = buildInitialState(token);
  }

  const handleSessionEnd = useCallback(
    (reason: SessionEndedReason): void => {
      klareoTokenRef.current = null;
      tokenExpiryRef.current = null;
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      removePersistedImpersonationToken();
      setToken(null);
      setStateRef.current({ ...SIGNED_OUT_STATE, error: null, sessionEndedReason: reason });
      console.info('[useImpersonationSession] Impersonation session ended', { reason });
    },
    [inactivityTimerRef, klareoTokenRef, sessionTimerRef, setStateRef, tokenExpiryRef],
  );

  // Handle impersonation token side-effects (refs and expiry timer).
  // The auth *state* is already set synchronously by the useState initializer
  // (buildInitialState), so this effect only handles the mutable refs and timers.
  useEffect(() => {
    if (!token) return;

    const payload = decodeJwtPayload(token);
    if (!payload) {
      console.warn('[useImpersonationSession] Discarding undecodable impersonation token');
      removePersistedImpersonationToken();
      setToken(null);
      return; // Invalid token — initial state already set the error
    }

    klareoTokenRef.current = token;

    const exp = payload.exp as number | undefined;
    if (!exp) return;

    const expiryMs = exp * 1000;
    const remainingMs = expiryMs - Date.now();
    tokenExpiryRef.current = expiryMs;

    if (remainingMs <= 0) {
      handleSessionEnd(SESSION_ENDED_REASON.MAX_DURATION);
      return;
    }

    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);

    sessionTimerRef.current = setTimeout(() => {
      handleSessionEnd(SESSION_ENDED_REASON.MAX_DURATION);
    }, remainingMs);
  }, [handleSessionEnd, klareoTokenRef, sessionTimerRef, token, tokenExpiryRef]);

  const clearImpersonation = useCallback((): void => {
    removePersistedImpersonationToken();
    setToken(null);
  }, []);

  // Fires only in *other* tabs. Overwrites (newValue !== null) are ignored so a
  // new impersonation started in another tab does not kill this one.
  useEffect(() => {
    if (!token) return;

    function handleStorageEvent(event: StorageEvent): void {
      if (isImpersonationTokenRemoval(event)) {
        handleSessionEnd(SESSION_ENDED_REASON.USER_LOGOUT);
      }
    }

    window.addEventListener('storage', handleStorageEvent);
    return (): void => window.removeEventListener('storage', handleStorageEvent);
  }, [handleSessionEnd, token]);

  return {
    clearImpersonation,
    initialState: initialStateRef.current,
    isImpersonating: token !== null,
    token,
  };
}

// --- Helpers ---

/** Removes the impersonation token from localStorage; never throws when storage is blocked. */
function removePersistedImpersonationToken(): void {
  try {
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  } catch (error: unknown) {
    console.warn('[useImpersonationSession] Failed to remove impersonation token from localStorage:', error);
  }
}

/** A `storage` event removing the impersonation key — either directly, or via `localStorage.clear()` (key=null). */
function isImpersonationTokenRemoval(event: StorageEvent): boolean {
  return (event.key === IMPERSONATION_TOKEN_KEY || event.key === null) && event.newValue === null;
}
